import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export default function LeaveManagement() {
  const navigate = useNavigate();
  const companyId = localStorage.getItem('selectedCompanyId');

  // ── State ──
  const [activeTab, setActiveTab] = useState('requests');
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [error, setError] = useState('');

  // Data
  const [leaveData, setLeaveData] = useState(null);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [balances, setBalances] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [pendingActions, setPendingActions] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [departments, setDepartments] = useState([]);

  // Filters
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [filterSearch, setFilterSearch] = useState('');

  // Calendar
  const [calMonth, setCalMonth] = useState(new Date().getMonth() + 1);
  const [calYear, setCalYear] = useState(new Date().getFullYear());

  // Modals
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [showTypeModal, setShowTypeModal] = useState(false);

  // Forms
  const [requestForm, setRequestForm] = useState({});
  const [approvalInfo, setApprovalInfo] = useState(null);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [adjustForm, setAdjustForm] = useState({});
  const [typeForm, setTypeForm] = useState({});
  const [employees, setEmployees] = useState([]);

  const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  // ── Fetch Data ──
  const fetchRequests = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterType) params.append('leave_type_id', filterType);
      if (filterDept) params.append('department_id', filterDept);
      if (filterSearch) params.append('search', filterSearch);

      const res = await fetch(`${API}/companies/${companyId}/leaves?${params}`, { headers: getAuthHeaders() });
      if (res.status === 401 || res.status === 403) { navigate('/login'); return; }
      const json = await res.json();
      if (json.status === 'success') setLeaveData(json.data);
    } catch { setError('Error loading leave data.'); }
    finally { setLoading(false); }
  }, [companyId, filterStatus, filterType, filterDept, filterSearch, navigate]);

  const fetchLeaveTypes = async () => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/leave-types`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.status === 'success') setLeaveTypes(json.data || []);
    } catch {}
  };

  const fetchBalances = async () => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/leave-balances`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.status === 'success') setBalances(json.data || []);
    } catch {}
  };

  const fetchCalendar = async () => {
    try {
      const params = new URLSearchParams({ month: calMonth, year: calYear });
      const res = await fetch(`${API}/companies/${companyId}/leave-calendar?${params}`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.status === 'success') setCalendarEvents(json.data || []);
    } catch {}
  };

  const fetchPending = async () => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/leave-pending`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.status === 'success') setPendingActions(json.data || []);
    } catch {}
  };

  const fetchAudit = async () => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/leave-audit`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.status === 'success') setAuditLogs(json.data || []);
    } catch {}
  };

  const fetchEmployees = async () => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/employees`, { headers: getAuthHeaders() });
      const json = await res.json();
      setEmployees(json.data || json.employees || []);
    } catch {}
  };

  const fetchDepts = async () => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/departments`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.data) setDepartments(json.data);
    } catch {}
  };

  useEffect(() => { if (companyId) { fetchRequests(); fetchLeaveTypes(); fetchDepts(); fetchEmployees(); } }, [fetchRequests, companyId]);

  useEffect(() => {
    if (activeTab === 'balances') fetchBalances();
    if (activeTab === 'calendar') fetchCalendar();
    if (activeTab === 'pending') fetchPending();
    if (activeTab === 'audit') fetchAudit();
    // eslint-disable-next-line
  }, [activeTab, calMonth, calYear]);

  // ── Actions ──
  const submitRequest = async () => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/leaves`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(requestForm),
      });
      const json = await res.json();
      if (json.status === 'success') {
        setShowRequestModal(false);
        fetchRequests();
        if (json.warnings?.length) window.alert('⚠️ ' + json.warnings.join('\n'));
      } else {
        window.alert(json.message || 'Error creating request.');
      }
    } catch { window.alert('Network error.'); }
  };

  const openApproval = async (leave) => {
    setSelectedLeave(leave);
    try {
      const res = await fetch(`${API}/companies/${companyId}/leaves/${leave.id}/approval-info`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.status === 'success') setApprovalInfo(json.data);
    } catch {}
    setShowApprovalModal(true);
  };

  const handleApprove = async (forceApprove = false) => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/leaves/${selectedLeave.id}/approve`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ force_approve: forceApprove }),
      });
      const json = await res.json();
      if (json.status === 'success') {
        setShowApprovalModal(false);
        fetchRequests();
        if (json.warnings?.length) window.alert('Approved with warnings:\n' + json.warnings.join('\n'));
      } else if (json.status === 'conflict') {
        if (window.confirm(`Attendance conflicts on: ${json.conflict_dates.join(', ')}.\n\nReplace existing attendance with leave?`)) {
          handleApprove(true);
        }
      } else {
        window.alert(json.message || 'Error approving.');
      }
    } catch { window.alert('Network error.'); }
  };

  const handleReject = async () => {
    const reason = window.prompt('Rejection reason:');
    if (reason === null) return;
    try {
      const res = await fetch(`${API}/companies/${companyId}/leaves/${selectedLeave.id}/reject`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      const json = await res.json();
      if (json.status === 'success') { setShowApprovalModal(false); fetchRequests(); }
      else window.alert(json.message || 'Error.');
    } catch { window.alert('Network error.'); }
  };

  const handleCancel = async (leaveId) => {
    const reason = window.prompt('Cancellation reason (optional):');
    if (reason === null) return;
    try {
      const res = await fetch(`${API}/companies/${companyId}/leaves/${leaveId}/cancel`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      const json = await res.json();
      if (json.status === 'success') fetchRequests();
      else window.alert(json.message || 'Error.');
    } catch { window.alert('Network error.'); }
  };

  const submitAdjustment = async () => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/leave-adjustments`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(adjustForm),
      });
      const json = await res.json();
      if (json.status === 'success') {
        setShowAdjustModal(false);
        setAdjustForm({});
        fetchBalances();
      } else window.alert(json.message || 'Error.');
    } catch { window.alert('Network error.'); }
  };

  const submitLeaveType = async () => {
    try {
      const method = typeForm.id ? 'PUT' : 'POST';
      const url = typeForm.id
        ? `${API}/companies/${companyId}/leave-types/${typeForm.id}`
        : `${API}/companies/${companyId}/leave-types`;
      const res = await fetch(url, {
        method,
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(typeForm),
      });
      const json = await res.json();
      if (json.status === 'success') {
        setShowTypeModal(false);
        setTypeForm({});
        fetchLeaveTypes();
      } else window.alert(json.message || 'Error.');
    } catch { window.alert('Network error.'); }
  };

  // ── Status styling ──
  const statusStyle = (status) => {
    const colors = {
      Draft: { bg: '#e9ecef', color: '#495057' },
      Pending: { bg: '#fff3cd', color: '#856404' },
      Approved: { bg: '#d4edda', color: '#155724' },
      Rejected: { bg: '#f8d7da', color: '#721c24' },
      Cancelled: { bg: '#d6d8db', color: '#383d41' },
    };
    const c = colors[status] || colors.Draft;
    return { ...styles.badge, background: c.bg, color: c.color };
  };

  if (!companyId) {
    return <div style={styles.container}><p style={{ color: '#dc3545' }}>No company selected.</p></div>;
  }

  // ═════════════════════════════════════════
  //  RENDER
  // ═════════════════════════════════════════
  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>🏖️ Leave Management</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={styles.btnSecondary} onClick={() => { setTypeForm({ name: '', code: '', color: '#3b82f6', is_paid: true, entitlement_days: 0, allow_negative_balance: false, allow_carryover: false, requires_approval: true }); setShowTypeModal(true); }}>⚙️ Leave Types</button>
          <button style={styles.btnPrimary} onClick={() => { setRequestForm({ employee_id: '', leave_type_id: '', start_date: '', end_date: '', duration_type: 'full_day', notes: '', status: 'Pending' }); setShowRequestModal(true); }}>+ New Request</button>
        </div>
      </div>

      {/* Summary Cards */}
      {leaveData?.summary && (
        <div style={styles.summaryRow}>
          {[
            ['📋', 'Total', leaveData.summary.total, '#0d6efd'],
            ['⏳', 'Pending', leaveData.summary.pending, '#ffc107'],
            ['✅', 'Approved', leaveData.summary.approved, '#198754'],
            ['❌', 'Rejected', leaveData.summary.rejected, '#dc3545'],
            ['🚫', 'Cancelled', leaveData.summary.cancelled, '#6c757d'],
            ['⚠️', 'Conflicts', leaveData.summary.with_conflicts, '#fd7e14'],
          ].map(([icon, label, val, color]) => (
            <div key={label} style={{ ...styles.summaryCard, borderLeft: `4px solid ${color}` }}>
              <div style={{ fontSize: 11, color: '#6c757d' }}>{icon} {label}</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{val}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div style={styles.tabRow}>
        {['requests','balances','calendar','pending','audit'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            style={activeTab === tab ? styles.tabActive : styles.tab}>
            {tab === 'requests' ? '📋 Requests' : tab === 'balances' ? '💰 Balances' : tab === 'calendar' ? '📅 Calendar' : tab === 'pending' ? '⚠️ Pending' : '📝 Audit'}
          </button>
        ))}
      </div>

      {error && <div style={styles.errorMsg}>{error}</div>}

      {/* ── TAB: REQUESTS ── */}
      {activeTab === 'requests' && (
        <>
          {/* Filters */}
          <div style={styles.filterRow}>
            <input type="text" placeholder="Search employee..." value={filterSearch} onChange={e => setFilterSearch(e.target.value)} style={styles.searchInput} />
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={styles.filterSelect}>
              <option value="">All Statuses</option>
              {['Draft','Pending','Approved','Rejected','Cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={filterType} onChange={e => setFilterType(e.target.value)} style={styles.filterSelect}>
              <option value="">All Types</option>
              {leaveTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <select value={filterDept} onChange={e => setFilterDept(e.target.value)} style={styles.filterSelect}>
              <option value="">All Departments</option>
              {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>

          {initialLoading ? <p>Loading...</p> : (
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Employee</th>
                    <th style={styles.th}>Type</th>
                    <th style={styles.th}>Dates</th>
                    <th style={styles.th}>Days</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Balance After</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(leaveData?.requests || []).map(req => (
                    <tr key={req.id}>
                      <td style={styles.td}>
                        <div style={{ fontWeight: 500 }}>{req.employee?.first_name} {req.employee?.last_name}</div>
                        <div style={{ fontSize: 11, color: '#6c757d' }}>{req.employee?.employee_code}</div>
                      </td>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, background: req.leave_type?.color || '#e9ecef', color: '#fff' }}>
                          {req.leave_type?.code}
                        </span>
                        <span style={{ marginLeft: 6, fontSize: 12 }}>{req.leave_type?.name}</span>
                      </td>
                      <td style={styles.td}>
                        <div style={{ fontSize: 13 }}>{req.start_date}</div>
                        {req.start_date !== req.end_date && <div style={{ fontSize: 11, color: '#6c757d' }}>→ {req.end_date}</div>}
                      </td>
                      <td style={{ ...styles.td, textAlign: 'center', fontWeight: 600 }}>{req.total_days}</td>
                      <td style={styles.td}><span style={statusStyle(req.status)}>{req.status}</span></td>
                      <td style={{ ...styles.td, textAlign: 'center', color: req.balance_after < 0 ? '#dc3545' : '#333', fontWeight: req.balance_after < 0 ? 600 : 400 }}>
                        {req.balance_after ?? '—'}
                      </td>
                      <td style={styles.td}>
                        {req.status === 'Pending' && (
                          <>
                            <button style={{ ...styles.smallBtn, color: '#198754' }} onClick={() => openApproval(req)} title="Review">👁️</button>
                          </>
                        )}
                        {['Pending', 'Approved'].includes(req.status) && (
                          <button style={{ ...styles.smallBtn, color: '#dc3545' }} onClick={() => handleCancel(req.id)} title="Cancel">✘</button>
                        )}
                        {req.has_conflict && !req.conflict_resolved && (
                          <span style={{ color: '#fd7e14', fontSize: 12 }} title="Attendance conflict">⚠️</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {(!leaveData?.requests?.length) && <tr><td style={styles.td} colSpan={7}>No leave requests found.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── TAB: BALANCES ── */}
      {activeTab === 'balances' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ margin: 0 }}>Leave Balances — {new Date().getFullYear()}</h3>
            <button style={styles.btnPrimary} onClick={() => { setAdjustForm({ employee_id: '', leave_type_id: '', type: 'manual_grant', amount: 0, reason: '' }); setShowAdjustModal(true); }}>+ Adjust Balance</button>
          </div>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Employee</th>
                <th style={styles.th}>Leave Type</th>
                <th style={styles.th}>Entitlement</th>
                <th style={styles.th}>Used</th>
                <th style={styles.th}>Grants</th>
                <th style={styles.th}>Carryover</th>
                <th style={styles.th}>Balance</th>
              </tr>
            </thead>
            <tbody>
              {balances.map(b => (
                <tr key={b.id}>
                  <td style={styles.td}>{b.employee?.first_name} {b.employee?.last_name}</td>
                  <td style={styles.td}>{b.leave_type?.name}</td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>{b.entitlement}</td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>{b.used}</td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>{b.manual_grants}</td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>{b.carryover}</td>
                  <td style={{ ...styles.td, textAlign: 'center', fontWeight: 700, color: b.balance < 0 ? '#dc3545' : '#198754' }}>{b.balance}</td>
                </tr>
              ))}
              {!balances.length && <tr><td style={styles.td} colSpan={7}>No balances found. Balances are created when leave requests are submitted.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* ── TAB: CALENDAR ── */}
      {activeTab === 'calendar' && (
        <div>
          <div style={styles.monthNav}>
            <button style={styles.navBtn} onClick={() => { if (calMonth === 1) { setCalMonth(12); setCalYear(y => y - 1); } else setCalMonth(m => m - 1); }}>◀</button>
            <span style={styles.monthLabel}>{monthNames[calMonth - 1]} {calYear}</span>
            <button style={styles.navBtn} onClick={() => { if (calMonth === 12) { setCalMonth(1); setCalYear(y => y + 1); } else setCalMonth(m => m + 1); }}>▶</button>
          </div>

          {calendarEvents.length === 0 ? <p style={{ color: '#6c757d' }}>No leave events this month.</p> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {calendarEvents.map(ev => (
                <div key={ev.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: '#fff', borderRadius: 8, borderLeft: `4px solid ${ev.color}`, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{ev.employee_name}</div>
                    <div style={{ fontSize: 12, color: '#6c757d' }}>{ev.leave_type} ({ev.leave_type_code})</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 13 }}>{ev.start_date} → {ev.end_date}</div>
                    <div style={{ fontSize: 12, color: '#6c757d' }}>{ev.total_days} day(s) · <span style={statusStyle(ev.status)}>{ev.status}</span></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── TAB: PENDING ── */}
      {activeTab === 'pending' && (
        <div>
          <h3 style={{ marginBottom: 12 }}>Pending Actions ({pendingActions.length})</h3>
          {!pendingActions.length ? <p style={{ color: '#6c757d' }}>No pending actions. ✅</p> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {pendingActions.map((item, idx) => (
                <div key={idx} style={{ padding: '12px 16px', background: '#fff', borderRadius: 8, borderLeft: `4px solid ${item.priority === 'high' ? '#dc3545' : '#ffc107'}`, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span style={{ ...styles.badge, background: item.type === 'pending_approval' ? '#fff3cd' : item.type === 'negative_balance' ? '#f8d7da' : '#cce5ff' }}>
                        {item.type.replace(/_/g, ' ')}
                      </span>
                      <span style={{ marginLeft: 8, fontWeight: 500 }}>
                        {item.leave?.employee?.first_name || item.balance?.employee?.first_name} {item.leave?.employee?.last_name || item.balance?.employee?.last_name}
                      </span>
                    </div>
                    {item.type === 'pending_approval' && (
                      <button style={styles.smallBtn} onClick={() => openApproval(item.leave)}>Review</button>
                    )}
                  </div>
                  {item.leave && <div style={{ fontSize: 12, color: '#6c757d', marginTop: 4 }}>{item.leave.leave_type?.name} · {item.leave.start_date} → {item.leave.end_date} · {item.leave.total_days} days</div>}
                  {item.balance && <div style={{ fontSize: 12, color: '#dc3545', marginTop: 4 }}>{item.balance.leave_type?.name} balance: {item.balance.balance} days</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── TAB: AUDIT ── */}
      {activeTab === 'audit' && (
        <div>
          <h3 style={{ marginBottom: 12 }}>Audit Log</h3>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Date/Time</th>
                <th style={styles.th}>User</th>
                <th style={styles.th}>Employee</th>
                <th style={styles.th}>Action</th>
                <th style={styles.th}>Details</th>
              </tr>
            </thead>
            <tbody>
              {auditLogs.map(log => (
                <tr key={log.id}>
                  <td style={{ ...styles.td, fontSize: 12 }}>{new Date(log.created_at).toLocaleString()}</td>
                  <td style={styles.td}>{log.user?.name || '—'}</td>
                  <td style={styles.td}>{log.employee ? `${log.employee.first_name} ${log.employee.last_name}` : '—'}</td>
                  <td style={styles.td}><span style={styles.badge}>{log.action}</span></td>
                  <td style={{ ...styles.td, fontSize: 11, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>{log.reason || log.new_value?.substring(0, 80) || '—'}</td>
                </tr>
              ))}
              {!auditLogs.length && <tr><td style={styles.td} colSpan={5}>No audit logs.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* ═══════════ MODALS ═══════════ */}

      {/* New Leave Request Modal */}
      {showRequestModal && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>New Leave Request</h3>
            <div style={styles.formGroup}>
              <label style={styles.label}>Employee</label>
              <select value={requestForm.employee_id || ''} onChange={e => setRequestForm(p => ({ ...p, employee_id: e.target.value }))} style={styles.input}>
                <option value="">— Select —</option>
                {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name} ({emp.employee_code})</option>)}
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Leave Type</label>
              <select value={requestForm.leave_type_id || ''} onChange={e => setRequestForm(p => ({ ...p, leave_type_id: e.target.value }))} style={styles.input}>
                <option value="">— Select —</option>
                {leaveTypes.map(t => <option key={t.id} value={t.id}>{t.code} — {t.name} ({t.is_paid ? 'Paid' : 'Unpaid'})</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ ...styles.formGroup, flex: 1 }}>
                <label style={styles.label}>Start Date</label>
                <input type="date" value={requestForm.start_date || ''} onChange={e => setRequestForm(p => ({ ...p, start_date: e.target.value }))} style={styles.input} />
              </div>
              <div style={{ ...styles.formGroup, flex: 1 }}>
                <label style={styles.label}>End Date</label>
                <input type="date" value={requestForm.end_date || ''} onChange={e => setRequestForm(p => ({ ...p, end_date: e.target.value }))} style={styles.input} />
              </div>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Duration Type</label>
              <select value={requestForm.duration_type || 'full_day'} onChange={e => setRequestForm(p => ({ ...p, duration_type: e.target.value }))} style={styles.input}>
                <option value="full_day">Full Day</option>
                <option value="partial_day">Partial Day (Half)</option>
                <option value="hours">Hours</option>
              </select>
            </div>
            {requestForm.duration_type === 'hours' && (
              <div style={styles.formGroup}>
                <label style={styles.label}>Hours</label>
                <input type="number" step="0.5" min="0.5" value={requestForm.total_hours || ''} onChange={e => setRequestForm(p => ({ ...p, total_hours: e.target.value }))} style={styles.input} />
              </div>
            )}
            <div style={styles.formGroup}>
              <label style={styles.label}>Notes</label>
              <textarea value={requestForm.notes || ''} onChange={e => setRequestForm(p => ({ ...p, notes: e.target.value }))} style={{ ...styles.input, height: 60 }} />
            </div>
            <div style={styles.modalActions}>
              <button style={styles.btnSecondary} onClick={() => setShowRequestModal(false)}>Cancel</button>
              <button style={styles.btnPrimary} onClick={submitRequest}>Submit</button>
            </div>
          </div>
        </div>
      )}

      {/* Approval Modal */}
      {showApprovalModal && selectedLeave && (
        <div style={styles.overlay}>
          <div style={{ ...styles.modal, maxWidth: 580 }}>
            <h3 style={styles.modalTitle}>Review Leave Request</h3>
            <div style={{ background: '#f8f9fa', borderRadius: 8, padding: 16, marginBottom: 16 }}>
              <p style={{ margin: '0 0 4px', fontWeight: 600 }}>{selectedLeave.employee?.first_name} {selectedLeave.employee?.last_name}</p>
              <p style={{ margin: '0 0 4px', fontSize: 13 }}>{selectedLeave.leave_type?.name} · {selectedLeave.start_date} → {selectedLeave.end_date}</p>
              <p style={{ margin: '0 0 4px', fontSize: 13 }}>{selectedLeave.total_days} day(s) · {selectedLeave.is_paid ? 'Paid' : 'Unpaid'}</p>
              {selectedLeave.notes && <p style={{ margin: 0, fontSize: 12, color: '#6c757d' }}>Notes: {selectedLeave.notes}</p>}
            </div>

            {approvalInfo && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                <div style={{ padding: 10, background: '#d4edda', borderRadius: 6 }}>
                  <div style={{ fontSize: 11, color: '#155724' }}>Current Balance</div>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>{approvalInfo.current_balance}</div>
                </div>
                <div style={{ padding: 10, background: approvalInfo.projected_balance < 0 ? '#f8d7da' : '#cce5ff', borderRadius: 6 }}>
                  <div style={{ fontSize: 11 }}>Projected Balance</div>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>{approvalInfo.projected_balance}</div>
                </div>
              </div>
            )}

            {approvalInfo?.is_negative && (
              <div style={{ background: '#fff3cd', padding: 10, borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
                ⚠️ This will result in a negative balance. {approvalInfo.negative_allowed ? 'Negative balance is permitted for this leave type.' : '⛔ Negative balance is NOT permitted.'}
              </div>
            )}

            {approvalInfo?.attendance_conflicts?.length > 0 && (
              <div style={{ background: '#fff3cd', padding: 10, borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
                ⚠️ Attendance records exist on: {approvalInfo.attendance_conflicts.map(c => c.date).join(', ')}
              </div>
            )}

            {approvalInfo?.overlapping_leaves?.length > 0 && (
              <div style={{ background: '#f8d7da', padding: 10, borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
                ⚠️ Overlapping leave: {approvalInfo.overlapping_leaves.map(l => `${l.leave_type?.name} (${l.start_date} - ${l.end_date})`).join(', ')}
              </div>
            )}

            <div style={styles.modalActions}>
              <button style={styles.btnDanger} onClick={handleReject}>Reject</button>
              <div style={{ flex: 1 }} />
              <button style={styles.btnSecondary} onClick={() => setShowApprovalModal(false)}>Close</button>
              <button style={styles.btnSuccess} onClick={() => handleApprove(false)}>✅ Approve</button>
            </div>
          </div>
        </div>
      )}

      {/* Adjustment Modal */}
      {showAdjustModal && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Adjust Leave Balance</h3>
            <div style={styles.formGroup}>
              <label style={styles.label}>Employee</label>
              <select value={adjustForm.employee_id || ''} onChange={e => setAdjustForm(p => ({ ...p, employee_id: e.target.value }))} style={styles.input}>
                <option value="">— Select —</option>
                {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</option>)}
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Leave Type</label>
              <select value={adjustForm.leave_type_id || ''} onChange={e => setAdjustForm(p => ({ ...p, leave_type_id: e.target.value }))} style={styles.input}>
                <option value="">— Select —</option>
                {leaveTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Adjustment Type</label>
              <select value={adjustForm.type || 'manual_grant'} onChange={e => setAdjustForm(p => ({ ...p, type: e.target.value }))} style={styles.input}>
                <option value="manual_grant">Manual Grant</option>
                <option value="carryover">Carryover</option>
                <option value="balance_adjustment">Balance Adjustment</option>
                <option value="expiry">Mark as Expired</option>
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Amount (days — positive to add, negative to deduct)</label>
              <input type="number" step="0.5" value={adjustForm.amount || ''} onChange={e => setAdjustForm(p => ({ ...p, amount: parseFloat(e.target.value) }))} style={styles.input} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Reason</label>
              <textarea value={adjustForm.reason || ''} onChange={e => setAdjustForm(p => ({ ...p, reason: e.target.value }))} style={{ ...styles.input, height: 60 }} />
            </div>
            <div style={styles.modalActions}>
              <button style={styles.btnSecondary} onClick={() => setShowAdjustModal(false)}>Cancel</button>
              <button style={styles.btnPrimary} onClick={submitAdjustment}>Apply</button>
            </div>
          </div>
        </div>
      )}

      {/* Leave Type Modal */}
      {showTypeModal && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>{typeForm.id ? 'Edit' : 'Create'} Leave Type</h3>
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ ...styles.formGroup, flex: 2 }}>
                <label style={styles.label}>Name</label>
                <input type="text" value={typeForm.name || ''} onChange={e => setTypeForm(p => ({ ...p, name: e.target.value }))} style={styles.input} placeholder="Annual Leave" />
              </div>
              <div style={{ ...styles.formGroup, flex: 1 }}>
                <label style={styles.label}>Code</label>
                <input type="text" value={typeForm.code || ''} onChange={e => setTypeForm(p => ({ ...p, code: e.target.value }))} style={styles.input} placeholder="AL" maxLength={10} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ ...styles.formGroup, flex: 1 }}>
                <label style={styles.label}>Entitlement (days/year)</label>
                <input type="number" step="0.5" value={typeForm.entitlement_days || ''} onChange={e => setTypeForm(p => ({ ...p, entitlement_days: parseFloat(e.target.value) }))} style={styles.input} />
              </div>
              <div style={{ ...styles.formGroup, flex: 1 }}>
                <label style={styles.label}>Color</label>
                <input type="color" value={typeForm.color || '#3b82f6'} onChange={e => setTypeForm(p => ({ ...p, color: e.target.value }))} style={{ ...styles.input, height: 38, padding: 2 }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 12 }}>
              {[
                ['is_paid', 'Paid Leave'],
                ['allow_negative_balance', 'Allow Negative Balance'],
                ['allow_carryover', 'Allow Carryover'],
                ['requires_approval', 'Requires Approval'],
                ['excess_becomes_unpaid', 'Excess Becomes Unpaid'],
              ].map(([key, label]) => (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                  <input type="checkbox" checked={typeForm[key] || false} onChange={e => setTypeForm(p => ({ ...p, [key]: e.target.checked }))} />
                  {label}
                </label>
              ))}
            </div>
            {typeForm.allow_carryover && (
              <div style={styles.formGroup}>
                <label style={styles.label}>Max Carryover Days</label>
                <input type="number" step="0.5" value={typeForm.max_carryover_days || ''} onChange={e => setTypeForm(p => ({ ...p, max_carryover_days: parseFloat(e.target.value) }))} style={styles.input} />
              </div>
            )}
            {typeForm.allow_negative_balance && (
              <div style={styles.formGroup}>
                <label style={styles.label}>Max Negative Days</label>
                <input type="number" step="0.5" value={typeForm.max_negative_days || ''} onChange={e => setTypeForm(p => ({ ...p, max_negative_days: parseFloat(e.target.value) }))} style={styles.input} />
              </div>
            )}
            <div style={styles.modalActions}>
              <button style={styles.btnSecondary} onClick={() => setShowTypeModal(false)}>Cancel</button>
              <button style={styles.btnPrimary} onClick={submitLeaveType}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════
//  STYLES
// ═══════════════════════════════════════
const styles = {
  container: { padding: 24, maxWidth: '100%', fontFamily: "'Segoe UI', sans-serif" },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 },
  title: { margin: 0, fontSize: 24, fontWeight: 700 },
  summaryRow: { display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' },
  summaryCard: { flex: '1 1 120px', background: '#fff', borderRadius: 8, padding: '10px 14px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderLeft: '4px solid #0d6efd' },
  filterRow: { display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' },
  searchInput: { flex: '1 1 200px', padding: '8px 12px', borderRadius: 6, border: '1px solid #ced4da', fontSize: 14, outline: 'none' },
  filterSelect: { padding: '8px 12px', borderRadius: 6, border: '1px solid #ced4da', fontSize: 14, outline: 'none', minWidth: 140 },
  tabRow: { display: 'flex', gap: 4, marginBottom: 16, borderBottom: '2px solid #e9ecef', paddingBottom: 0 },
  tab: { background: 'none', border: 'none', padding: '8px 16px', cursor: 'pointer', fontSize: 14, color: '#6c757d', borderBottom: '2px solid transparent', marginBottom: -2 },
  tabActive: { background: 'none', border: 'none', padding: '8px 16px', cursor: 'pointer', fontSize: 14, color: '#0d6efd', fontWeight: 600, borderBottom: '2px solid #0d6efd', marginBottom: -2 },
  errorMsg: { background: '#f8d7da', color: '#842029', padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 14 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { background: '#f1f3f5', padding: '10px 8px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' },
  td: { padding: '8px', borderBottom: '1px solid #eee', whiteSpace: 'nowrap' },
  badge: { padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, display: 'inline-block' },
  smallBtn: { background: 'none', border: '1px solid #dee2e6', borderRadius: 4, padding: '3px 8px', cursor: 'pointer', fontSize: 14, marginRight: 4 },
  monthNav: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16, justifyContent: 'center' },
  navBtn: { background: '#e9ecef', border: 'none', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 16, fontWeight: 600 },
  monthLabel: { fontSize: 20, fontWeight: 600, minWidth: 160, textAlign: 'center' },
  btnPrimary: { background: '#0d6efd', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', cursor: 'pointer', fontSize: 14, fontWeight: 500 },
  btnSecondary: { background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', cursor: 'pointer', fontSize: 14, fontWeight: 500 },
  btnSuccess: { background: '#198754', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', cursor: 'pointer', fontSize: 14, fontWeight: 500 },
  btnDanger: { background: '#dc3545', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', cursor: 'pointer', fontSize: 14, fontWeight: 500 },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#fff', borderRadius: 12, padding: 24, width: '90%', maxWidth: 520, maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' },
  modalTitle: { margin: '0 0 16px', fontSize: 18, fontWeight: 600 },
  modalActions: { display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' },
  formGroup: { marginBottom: 12 },
  label: { display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4, color: '#333' },
  input: { width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ced4da', fontSize: 14, outline: 'none', boxSizing: 'border-box' },
};